import { Check } from "lucide-react";
import type { BuyerListingAssociationRowDto } from "@/api/buyers";
import { StatusBadge } from "@/design-system/components/ui";
import { buyerLabel } from "../buyerFilters";
import { submissionStatusTone } from "../buyerListBits";
import { associationsCopy as copy } from "./associationsCopy";

/**
 * The derived status badge for one associated listing.
 *
 * 🔴 **The association stores no status of its own, and must not.** The badge is
 * read from the attached submission every time the list is rendered, because the
 * copy that drifts is the one the list renders — a status denormalised onto the
 * association would go stale the moment the submission moved, and this list is
 * exactly where a specialist looks to decide what to work next. The server
 * derives it in the same round trip (`BuyerListingAssociationRowDto`), so there
 * is no second source here either.
 *
 * A null `submissionStatus` means **not yet worked**: the buyer is looking at
 * this listing and nobody has presented it. That is a distinct state from any
 * value in the funnel, so it gets its own neutral label rather than being
 * collapsed into the first status — showing "Shortlisted" for it would claim a
 * funnel position nobody has reached.
 *
 * The tone comes from `submissionStatusTone`, shared with the submissions list
 * and the buyer's Submissions tab, so one status is never one colour here and
 * another there.
 */
export function AssociationStatusBadge({ row }: { row: BuyerListingAssociationRowDto }) {
  if (!row.submissionStatus) {
    return (
      <StatusBadge tone="neutral" data-testid="association-status">
        Not yet worked
      </StatusBadge>
    );
  }

  return (
    <StatusBadge tone={submissionStatusTone(row.submissionStatus)} data-testid="association-status">
      {buyerLabel(row.submissionStatus)}
    </StatusBadge>
  );
}

/**
 * City / state as one cell, or `null` when neither is recorded. Both parts are
 * independently nullable on the wire, so neither can be assumed present.
 *
 * <p>🔴 It returns NULL rather than its own em dash. The dash belongs to
 * {@link MissingValue}, which also carries the "Not recorded" accessible name —
 * a bare "—" is punctuation a screen reader either reads aloud as a symbol or
 * skips, and either way the listener cannot tell an empty cell from a cell that
 * failed to render. A helper that formats its own missing case is a second
 * place the rule is written down.</p>
 */
export function locationOf(row: BuyerListingAssociationRowDto): string | null {
  return [row.listingCity, row.listingState].filter(Boolean).join(", ") || null;
}

/**
 * The words the status badge renders, as plain text.
 *
 * <p>Exported so a table's search can match what a reader actually SEES.
 * `SubTable` falls back to a cell's rendered text, which for a badge means
 * whatever ended up inside the element — so deriving the label twice, once for
 * display and once for search, is how the two quietly stop agreeing. One
 * function, both callers.</p>
 */
export function associationStatusText(row: BuyerListingAssociationRowDto): string {
  return row.submissionStatus ? buyerLabel(row.submissionStatus) : "Not yet worked";
}

/**
 * R3/D16 — the "Presented" marker beside a listing's name: the buyer already
 * has a standing submission for it (`submissionId`, archived included). The
 * Research tab's "Attached" pill, so one fact reads one way across the buyer.
 * The Status column beside it is unchanged and still says how far along it is.
 */
export function PresentedBadge() {
  return (
    <StatusBadge tone="ok" className="shrink-0" data-testid="association-presented-badge">
      <Check size={12} aria-hidden />
      {copy.presented}
    </StatusBadge>
  );
}

/**
 * The row's present control's label, sized to the WIDER of its two states.
 *
 * <p>Both labels occupy one grid cell and the inactive one is invisible (and
 * hidden from assistive tech), so PRESENT THIS ONE and the disabled PRESENTED ✓
 * that replaces it are the same width — a row flipping after a bulk present
 * does not shift the rest of its actions.</p>
 */
export function PresentControlLabel({ presented }: { presented: boolean }) {
  const presentLabel = <span>{copy.presentThisOne}</span>;
  const presentedLabel = (
    <span className="inline-flex items-center gap-space-1">
      {copy.presentedButton}
      <Check size={14} aria-hidden />
    </span>
  );
  return (
    <span className="inline-grid justify-items-center">
      <span className="col-start-1 row-start-1">{presented ? presentedLabel : presentLabel}</span>
      <span className="invisible col-start-1 row-start-1" aria-hidden>
        {presented ? presentLabel : presentedLabel}
      </span>
    </span>
  );
}
